import { useEffect } from "react"
import { useSignal } from "@preact/signals-react"
import { postSubmit } from "@ui-seeker/utils"

import { ANNOUNCEMENTS_APIS } from "@constants"
import { notifyAnnouncementSeen } from "@signals"

/**
 * Feeds the news popup: what this user has pending, and marking each one as read.
 *
 * Failures are swallowed on purpose. An announcement is the least important thing on
 * screen, and a 500 here must never stand between the user and their dashboard — this hook
 * runs on **every** page load, for every user.
 */
export default function useAnnouncementsFeed() {
    const pending = useSignal([])
    const index = useSignal(0)
    const markedIds = useSignal(new Set())

    useEffect(() => {
        postSubmit(ANNOUNCEMENTS_APIS.PENDING, {})
            .then(res => { pending.value = res?.data ?? [] })
            .catch(() => { pending.value = [] })
    }, [])

    /** Only announcements the user actually reached are marked; the rest come back next
     *  session. Sent at most once per id, and never awaited — closing the popup must feel
     *  instant even if the request is slow. */
    const markSeen = announcementID => {
        if (!announcementID || markedIds.value.has(announcementID)) return
        markedIds.value = new Set(markedIds.value).add(announcementID)
        postSubmit(ANNOUNCEMENTS_APIS.SEEN, { announcementID })
            // The bell owns the badge and has no idea this happened. Without the notice its
            // count stays at the number it had when the app loaded.
            .then(() => notifyAnnouncementSeen())
            .catch(() => {})
    }

    const current = pending.value[index.value] ?? null

    const next = () => {
        markSeen(current?.announcementID)
        index.value = index.value + 1
    }

    const dismissAll = () => {
        markSeen(current?.announcementID)
        index.value = pending.value.length
    }

    return {
        current,
        position: index.value + 1,
        total: pending.value.length,
        hasPending: index.value < pending.value.length,
        next,
        dismissAll,
    }
}
